// 상품 목록의 소스 판정은 results-source 한 곳에서만 한다 (컬렉션 소스 · 섹션 렌더 · 이후 검색 API)
import { fetchResultsHTML, applyResultsHTML } from '@theme/results-source';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * 기본 정렬(리뷰 많은 순)의 테마 내부 센티넬. URL 로 내보내지 않는다 — createURLParameters 가 지운다.
 * sort_by 가 없으면 검색 API 는 기본 인덱스(리뷰 많은 순)를, Shopify 폴백은 컬렉션 관리자 정렬을 쓴다.
 * MD 추천순은 이 센티넬이 아니라 별도 옵션 `manual` 이다 (docs/SORTING.md)
 */
const RECOMMENDS_SORT = 'recommends';

/** 정렬 컴포넌트에 data-default-sort 가 없을 때 쓰는 폴백 */
const DEFAULT_SORT_FALLBACK = RECOMMENDS_SORT;

// ─── 트래킹 helpers (검색·콜렉션 페이지 공통) ───────────────────────────────
/**
 * 페이지 컨텍스트 기반 tracking_id 합성. `<results-list data-tracking-id="...">`
 * 또는 `<dialog-component#filters-drawer data-tracking-id="...">` 가 있으면 그 값을
 * 우선 사용. 페이지 안의 다른 컴포넌트(검색 input 등) 도 `data-tracking-id` 를
 * 자체 용도로 쓰므로 셀렉터를 facets 영역 element 로 좁힌다.
 * @returns {string}
 */
function _facetsTrackingId() {
  const el = document.querySelector(
    'results-list[data-tracking-id], #filters-drawer[data-tracking-id]'
  );
  if (el instanceof HTMLElement && el.dataset.trackingId) return el.dataset.trackingId;
  return 'search-results';
}

/**
 * 인터랙션이 일어난 영역(`drawer` / `vertical` / `horizontal`) 식별.
 * @param {Element | null | undefined} el
 * @returns {'drawer' | 'vertical' | 'horizontal' | 'unknown'}
 */
function _facetsPlacement(el) {
  if (!(el instanceof Element)) return 'unknown';
  if (el.closest('.facets--drawer')) return 'drawer';
  if (el.closest('.facets--vertical')) return 'vertical';
  if (el.closest('.facets--horizontal') || el.closest('.facets-mobile-bar')) return 'horizontal';
  return 'unknown';
}

/** @type {'apply' | 'cancel' | null} 다음 dialog:close 이벤트의 reason 마킹 */
let _facetsDrawerCloseReason = null;

/** @type {string | null} 다음 dialog:open 이벤트의 entry_point 마킹 */
let _facetsDrawerEntryPoint = null;
/** @type {string | null} entry_point=mobile_filter_button 일 때 동반되는 filter param name */
let _facetsDrawerFilterParamName = null;

/**
 * `#filters-drawer` 의 dialog:open / dialog:close 를 트래킹과 연결.
 * 캡처-페이즈 click 로 `data-track-drawer-trigger` 어트리뷰트가 있는 트리거를 감지해
 * entry_point 를 다음 open 이벤트에 실어 보낸다. close reason 은
 * `applyAndClose` 가 미리 마킹한 `_facetsDrawerCloseReason` 을 소비.
 */
function _setupFiltersDrawerTracking() {
  const drawer = document.getElementById('filters-drawer');
  if (!drawer || /** @type {any} */ (drawer).__gbHooked) return;
  /** @type {any} */ (drawer).__gbHooked = true;

  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const trigger = target.closest('[data-track-drawer-trigger]');
    if (trigger instanceof HTMLElement) {
      _facetsDrawerEntryPoint = trigger.dataset.trackDrawerTrigger || 'unknown';
      _facetsDrawerFilterParamName = trigger.getAttribute('data-filter-param-name') || null;
    }
  }, true);

  drawer.addEventListener('dialog:open', () => {
    if (!window.GBTrack) return;
    const props = {
      tracking_id: _facetsTrackingId(),
      entry_point: _facetsDrawerEntryPoint || 'unknown',
    };
    if (_facetsDrawerFilterParamName) {
      /** @type {any} */ (props).filter_param_name = _facetsDrawerFilterParamName;
    }
    window.GBTrack.track('search_filter_drawer_open', props);
    _facetsDrawerEntryPoint = null;
    _facetsDrawerFilterParamName = null;
  });

  drawer.addEventListener('dialog:close', () => {
    if (!window.GBTrack) return;
    window.GBTrack.track('search_filter_drawer_close', {
      tracking_id: _facetsTrackingId(),
      reason: _facetsDrawerCloseReason || 'cancel',
    });
    _facetsDrawerCloseReason = null;
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _setupFiltersDrawerTracking);
  } else {
    _setupFiltersDrawerTracking();
  }
}

/**
 * 섹션에 설정된 기본 정렬값. `sorting-filter-component[data-default-sort]` 에서 읽음.
 * 정렬 컴포넌트가 없으면 DEFAULT_SORT_FALLBACK.
 * @returns {string}
 */
function _getDefaultSort() {
  const el = document.querySelector('sorting-filter-component[data-default-sort]');
  if (el instanceof HTMLElement && el.dataset.defaultSort) return el.dataset.defaultSort;
  return DEFAULT_SORT_FALLBACK;
}

/**
 * 현재 적용해야 할 정렬값. URL의 sort_by 우선, 없으면 섹션 기본값.
 * @returns {string}
 */
function _getEffectiveSort() {
  return new URL(window.location.href).searchParams.get('sort_by') || _getDefaultSort();
}

/**
 * Section 재렌더(morph) 후 URL의 sort_by 값에 맞게 정렬 컴포넌트 UI를 보정.
 * @param {string} [sortValue] - 강제 지정할 sort 값. 생략 시 _getEffectiveSort()로 결정 (URL sort_by → 섹션 기본값)
 */
function _updateSortingComponentUI(sortValue) {
  const targetSort = sortValue || _getEffectiveSort();

  document.querySelectorAll('sorting-filter-component').forEach((component) => {
    const targetOption = /** @type {HTMLElement | null} */ (
      component.querySelector(`[role="option"][data-value="${targetSort}"]`)
    );
    if (!targetOption) return;

    // 현재 선택된 옵션의 체크마크를 target 옵션으로 이동
    const prevSelected = component.querySelector('[role="option"][aria-selected="true"]');
    const checkmark = prevSelected?.querySelector('.sorting-filter__check');
    if (checkmark) {
      checkmark.remove();
      targetOption.appendChild(checkmark);
    }

    // aria-selected / tabindex 업데이트
    component.querySelectorAll('[role="option"]').forEach((option) => {
      const isActive = /** @type {HTMLElement} */ (option).dataset.value === targetSort;
      option.setAttribute('aria-selected', isActive ? 'true' : 'false');
      option.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // 트리거 표시 텍스트 업데이트 (데스크탑/모바일)
    const labelText = targetOption.querySelector('.sorting-filter__option-text')?.textContent?.trim();
    if (labelText) {
      ['[ref="currentValue"]', '[ref="mobileCurrentValue"]'].forEach((sel) => {
        const el = component.querySelector(sel);
        if (el) el.textContent = labelText;
      });
    }

    // 라디오 인풋 checked 상태 업데이트
    component.querySelectorAll('input[type="radio"][name="sort_by"]').forEach((radio) => {
      /** @type {HTMLInputElement} */ (radio).checked = radio.value === targetSort;
    });
  });
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /** @type {string | null} 대기 중인 섹션 HTML (morph 전) */
  #pendingHTML = null;
  /** @type {AbortController | null} */
  #pendingAbortController = null;
  /** @type {boolean} 미적용 필터 변경 존재 여부 */
  #hasPendingFilters = false;

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    // 기본 정렬은 URL 에 내보내지 않는다 (docs/SORTING.md §1).
    // - recommends: 리뷰 많은 순 센티넬
    // - 그 지면의 기본값(data-default-sort): 수동 정렬 컬렉션의 MD 추천순, 테마 에디터에서 고른 판매량순 등.
    //   그 지면은 파라미터 없음 = 기본값이다. 리뷰 많은 순은 'most-reviewed' 로 URL 에 실린다 (snippets/sorting.liquid)
    const sortBy = newParameters.get('sort_by');
    if (sortBy === RECOMMENDS_SORT || sortBy === _getDefaultSort()) {
      newParameters.delete('sort_by');
    }

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    // 브랜드(vendor) 필터: URL 에 있으나 폼에 체크박스가 아예 없는 값만 유지.
    // (현재 결과에 vendor facet 값이 없어 체크박스 미렌더 → 선택 유지)
    // 체크박스가 존재하는데 해제된 경우는 사용자의 명시적 해제이므로 다시 추가하지 않는다.
    const url = new URL(window.location.href);
    const form = this.refs.facetsForm;
    const vendorInputValues = new Set(
      form
        ? Array.from(
            form.querySelectorAll('input[name="filter.p.m.custom.vendor"]')
          ).map((/** @type any */ input) => input.value)
        : []
    );
    const selectedVendors = new Set(newParameters.getAll('filter.p.m.custom.vendor'));
    for (const vendorFilter of url.searchParams.getAll('filter.p.m.custom.vendor')) {
      if (selectedVendors.has(vendorFilter)) continue;
      if (vendorInputValues.has(vendorFilter)) continue;
      newParameters.append('filter.p.m.custom.vendor', vendorFilter);
      selectedVendors.add(vendorFilter);
    }

    // GNB 카테고리(2차) 필터는 폼 facet 이 아닌 URL 내비게이션 파라미터이므로 적용 시에도 유지.
    // 단, 캐스케이딩 카테고리 필터(category-drilldown)가 있으면 폼이 2차의 진실이므로 URL 보존을 끈다
    // (그래야 2차 해제가 results 카운트에 반영됨).
    const hasCascadingCategoryFilter = !!(form && form.querySelector('category-drilldown'));
    const categoryDepth2 = url.searchParams.get('filter.p.m.custom.category_depth_2');
    if (!hasCascadingCategoryFilter && categoryDepth2 && !newParameters.has('filter.p.m.custom.category_depth_2')) {
      newParameters.set('filter.p.m.custom.category_depth_2', categoryDepth2);
    }

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * 검색어가 비어있으면 *를 기본값으로 사용 (.claude/commands/search-page-rules.md 참조)
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    const query = url.searchParams.get(SEARCH_QUERY);
    return query && query.trim() !== '' ? query : '*';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   * URL에 q=*는 표시하지 않음 (.claude/commands/search-page-rules.md 참조)
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      // q=* 인 경우 URL에 표시하지 않음 (사용자에게 숨김)
      if (param === SEARCH_QUERY && value === '*') continue;
      url.searchParams.append(param, value);
    }

    history.replaceState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * /search 페이지 여부 (로케일 프리픽스 포함: /fr/search 등).
   * /search 에서는 필터 변경 즉시 URL을 바꾸지 않고 "결과 보기" 적용 시점에만 반영한다.
   * @returns {boolean}
   */
  #isSearchPage() {
    return /\/search\/?$/.test(window.location.pathname);
  }

  /**
   * Updates filters and renders the section
   * @param {Event} [event] - 체크박스 change 이벤트 (트래킹용 — 직접 호출 시 생략)
   */
  updateFilters = async (event) => {
    if (
      event &&
      event.target instanceof HTMLInputElement &&
      event.target.type === 'checkbox' &&
      window.GBTrack
    ) {
      const input = event.target;
      const details = input.closest('details');
      const labelText =
        input.closest('li')?.querySelector('.checkbox__label-text')?.textContent?.trim() ||
        input.closest('label')?.textContent?.trim() ||
        input.value;
      window.GBTrack.track('search_filter_value_toggle', {
        tracking_id: _facetsTrackingId(),
        filter_label: details instanceof HTMLElement ? details.dataset.filterLabel || null : null,
        filter_param_name: input.name,
        filter_value: input.value,
        filter_value_label: labelText,
        action: input.checked ? 'select' : 'deselect',
        placement: _facetsPlacement(input),
      });
    }
    // /search: URL은 "결과 보기"(applyPendingFilters) 시점으로 미룬다. 그 외 페이지는 기존대로 즉시 반영.
    if (!this.#isSearchPage()) this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    // 섹션 렌더링 전에 즉시 모바일 필터 버튼 카운팅 업데이트 (UI 즉시 반영)
    this.#syncMobileFilterButtons();
    await this.#previewFilters();
  };

  /**
   * Updates the section
   * q=*가 포함된 URL을 명시적으로 전달하여 검색어 없을 때도 필터가 동작하도록 함
   * (.claude/commands/search-page-rules.md 참조)
   * @returns {Promise<string>}
   */
  async #updateSection() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    // URL 파라미터를 명시적으로 설정 (q=* 포함)
    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    const html = await fetchResultsHTML(this.sectionId, url, !Shopify.designMode);
    applyResultsHTML(this.sectionId, html);
    // morph 후 서버가 정렬 UI를 기본값으로 리셋할 수 있으므로 URL 기준으로 보정
    _updateSortingComponentUI();
  }

  /**
   * 필터 변경 시 섹션 HTML을 fetch하되 morph하지 않고, 카운트만 업데이트.
   * "결과 보기" 버튼 클릭 시 캐시된 HTML로 즉시 morph.
   */
  async #previewFilters() {
    if (this.#pendingAbortController) this.#pendingAbortController.abort();
    this.#pendingAbortController = new AbortController();
    const { signal } = this.#pendingAbortController;

    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();
    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    const html = await fetchResultsHTML(this.sectionId, url, false);
    if (signal.aborted) return;

    this.#pendingHTML = html;
    this.#hasPendingFilters = true;
    this.#updateCountDisplays(html);
    // 서버 계산 카운트로 모바일 필터 버튼 뱃지를 재확정(즉시 JS 재계산 보정)
    this.#reconcileMobileButtonsFromHTML(html);
  }

  /**
   * fetch한 HTML에서 상품 카운트를 파싱하여 화면의 카운트 표시를 업데이트
   * @param {string} html
   */
  #updateCountDisplays(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const countEl = doc.querySelector('[data-testid="products-count"] span')
      || doc.querySelector('[data-testid="products-count-mobile"] span');
    let countText = countEl?.textContent?.trim();
    // 0 결과: search/collection 섹션은 결과가 0이면 툴바(products-count)를 렌더하지 않고
    // "결과 없음" 상태만 렌더한다. 이 경우 count 요소가 없어 early-return 되면 "결과 보기"
    // 카운트가 직전 값으로 남으므로, 유효한 섹션 HTML이면 (0)으로 명시 갱신한다.
    if (!countText && doc.body && doc.body.children.length > 0) countText = '(0)';
    if (!countText) return;

    // 프리뷰(필터 선택)에서는 "결과 보기" 버튼 카운트만 갱신한다.
    // products-count-wrapper(툴바 상품 수)는 실제 상품 목록이 바뀌는
    // "결과 보기" 적용(morph) 시점에 섹션 재렌더로만 갱신한다.
    document.querySelectorAll('.facets__see-results span').forEach((el) => {
      el.textContent = countText;
    });
  }

  /**
   * 대기 중인 필터 변경을 적용 (캐시된 HTML로 DOM morph)
   */
  async applyPendingFilters() {
    if (!this.#pendingHTML || !this.#hasPendingFilters) return;
    // /search: 변경 시점에 미뤄둔 URL 갱신을 적용 시점에 수행
    if (this.#isSearchPage()) this.#updateURLHash();
    applyResultsHTML(this.sectionId, this.#pendingHTML);
    _updateSortingComponentUI();
    this.#syncDrawerCheckboxes();
    // morph로 모바일 필터 버튼은 이미 서버 카운트로 재렌더됨.
    // 이 시점 체크박스 DOM은 data-skip-node-update로 stale 하므로 재계산하면 올바른 카운트를 덮어씀.
    this.#pendingHTML = null;
    this.#hasPendingFilters = false;
  }

  /**
   * 정렬 변경 등 즉시 적용이 필요한 경우 (deferred 우회)
   */
  updateFiltersImmediate = async () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#syncMobileFilterButtons();
    this.#pendingHTML = null;
    this.#hasPendingFilters = false;
    await this.#updateSection();
  };

  /**
   * "결과 보기" 버튼에서 호출: 필터 적용 + 다이얼로그 닫기
   */
  applyAndClose = async () => {
    if (window.GBTrack) {
      const params = this.createURLParameters();
      let pendingFilterCount = 0;
      for (const key of params.keys()) {
        if (key.startsWith('filter.')) pendingFilterCount++;
      }
      const countText = document
        .querySelector('.facets__see-results span')
        ?.textContent?.replace(/[()]/g, '')
        .trim();
      const pendingResultsCount = countText ? Number(countText.replace(/[^0-9]/g, '')) : null;
      window.GBTrack.track('search_filter_apply_click', {
        tracking_id: _facetsTrackingId(),
        placement: this.closest('.facets--drawer') ? 'drawer' : 'desktop_vertical_sticky',
        pending_filter_count: pendingFilterCount,
        pending_results_count: Number.isFinite(pendingResultsCount) ? pendingResultsCount : null,
      });
    }
    _facetsDrawerCloseReason = 'apply';
    await this.applyPendingFilters();
    const dialog = this.closest('dialog-component')
      || document.querySelector('#filters-drawer');
    if (dialog && typeof /** @type {any} */ (dialog).closeDialog === 'function') {
      /** @type {any} */ (dialog).closeDialog();
    }
  };

  /**
   * Updates filters based on a provided URL
   * Clear all 버튼 클릭 시 URL에서 직접 파라미터를 파싱하여 섹션 업데이트
   * @param {string} url - The URL to update filters with
   */
  async updateFiltersByURL(url) {
    // URL 파싱하여 새 파라미터 추출
    const parsedUrl = new URL(url, window.location.origin);
    const newParams = new URLSearchParams(parsedUrl.search);

    // 상대 URL인 경우 현재 페이지의 pathname 사용
    const pathname = parsedUrl.pathname === '/' && url.startsWith('?')
      ? window.location.pathname
      : parsedUrl.pathname;

    // 검색어가 없으면 *를 기본값으로 사용 (.claude/commands/search-page-rules.md 참조)
    if (!newParams.has(SEARCH_QUERY) || newParams.get(SEARCH_QUERY)?.trim() === '') {
      newParams.set(SEARCH_QUERY, '*');
    }

    // GNB 카테고리(2차) 필터는 폼 facet 이 아닌 URL 내비게이션 파라미터이므로 리셋 시에도 유지.
    // 단, 캐스케이딩 카테고리 필터가 있으면 2차도 일반 facet 이므로 보존하지 않는다(칩/전체해제로 제거 가능).
    const hasCascadingCategoryFilter = !!this.refs.facetsForm?.querySelector('category-drilldown');
    const categoryDepth2 = new URL(window.location.href).searchParams.get('filter.p.m.custom.category_depth_2');
    if (!hasCascadingCategoryFilter && categoryDepth2 && !newParams.has('filter.p.m.custom.category_depth_2')) {
      newParams.set('filter.p.m.custom.category_depth_2', categoryDepth2);
    }

    // 필터가 모두 해제되는지 확인 (리셋 여부).
    // category_depth_2 는 내비게이션 파라미터이므로 제외하되, 캐스케이딩 필터에선 일반 facet 으로 취급한다.
    const isReset = !Array.from(newParams.keys()).some(
      (key) => key.startsWith('filter.') && (hasCascadingCategoryFilter || key !== 'filter.p.m.custom.category_depth_2')
    );

    // 리셋인 경우 즉시 drawer 체크박스 모두 해제 (UI 즉시 반영)
    if (isReset) {
      this.#clearAllDrawerCheckboxes();
    }

    // URL 업데이트 (q=*는 표시하지 않음)
    const displayUrl = new URL(pathname, window.location.origin);
    for (const [param, value] of newParams.entries()) {
      if (param === SEARCH_QUERY && value === '*') continue;
      displayUrl.searchParams.append(param, value);
    }
    history.replaceState('', '', displayUrl.toString());

    // 이벤트 발생
    this.dispatchEvent(new FilterUpdateEvent(newParams));

    // 섹션 업데이트 (q=* 포함된 URL 사용)
    const fetchUrl = new URL(pathname, window.location.origin);
    for (const [param, value] of newParams.entries()) {
      fetchUrl.searchParams.append(param, value);
    }

    const drawerHTML = await fetchResultsHTML(this.sectionId, fetchUrl, !Shopify.designMode);
    applyResultsHTML(this.sectionId, drawerHTML);
    _updateSortingComponentUI();

    // drawer 내부의 체크박스를 새로 렌더링된 섹션과 동기화
    this.#syncDrawerCheckboxes();

    // 모바일 필터 버튼은 renderSection으로 서버 카운트가 이미 반영됨.
    // 이 시점 체크박스 DOM은 stale 하므로 재계산하면 올바른 카운트를 덮어씀.
  }

  /**
   * 모든 폼의 체크박스와 가격 input을 해제
   */
  #clearAllDrawerCheckboxes() {
    // 모든 facets-form-component에서 체크박스 해제
    /** @type {NodeListOf<HTMLInputElement>} */
    const allCheckboxes = document.querySelectorAll('facets-form-component input[type="checkbox"]:checked');
    allCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    // 모든 facets-form-component에서 price input 초기화
    /** @type {NodeListOf<HTMLInputElement>} */
    const allPriceInputs = document.querySelectorAll('facets-form-component input[name*="filter.v.price"]');
    allPriceInputs.forEach((input) => {
      input.value = '';
    });
  }

  /**
   * Drawer 내부의 체크박스를 적용된 필터(URL) 상태와 동기화.
   * 체크박스 input 은 data-skip-node-update 라 morph 후에도 checked 가 갱신되지 않는다.
   * 메인 폼 역시 같은 이유로 stale 할 수 있으므로, 적용 필터의 단일 진실 공급원인 URL 을 기준으로 맞춘다.
   */
  #syncDrawerCheckboxes() {
    const drawerForm = document.querySelector('.facets--drawer facets-form-component');
    if (!drawerForm) return;

    const params = new URLSearchParams(window.location.search);

    /** @type {NodeListOf<HTMLInputElement>} */
    const drawerCheckboxes = drawerForm.querySelectorAll('input[type="checkbox"]');
    drawerCheckboxes.forEach((checkbox) => {
      const shouldCheck = params.getAll(checkbox.name).includes(checkbox.value);
      if (checkbox.checked !== shouldCheck) {
        checkbox.checked = shouldCheck;
      }
    });
  }

  /**
   * 모바일 필터 버튼의 카운팅을 현재 폼 상태와 동기화
   */
  #syncMobileFilterButtons() {
    const mobileBar = document.querySelector('.facets-mobile-bar__filters');
    if (!mobileBar) return;

    // drawer 폼 또는 현재 폼에서 체크박스 상태 확인
    const drawerForm = document.querySelector('.facets--drawer facets-form-component');
    const form = drawerForm || this;

    let totalActiveFilters = 0;

    // 모바일 필터 버튼들을 순회하며 카운팅 업데이트
    /** @type {NodeListOf<HTMLButtonElement>} */
    const filterButtons = mobileBar.querySelectorAll('.facets-mobile-bar__filter-button');
    filterButtons.forEach((button) => {
      const filterLabel = button.dataset.filter;
      if (!filterLabel) return;

      // 해당 필터 라벨에 해당하는 패널 찾기
      const panel = form.querySelector(`details[data-filter-label="${filterLabel}"]`);
      if (!panel) return;

      let count = 0;

      // 가격 필터인지 확인
      const isPriceFilter = panel.dataset.filterType === 'price_range';

      if (isPriceFilter) {
        // 가격 필터: 최소 또는 최대 값이 입력되어 있으면 1
        /** @type {HTMLInputElement | null} */
        const minInput = panel.querySelector('input[ref="minInput"]');
        /** @type {HTMLInputElement | null} */
        const maxInput = panel.querySelector('input[ref="maxInput"]');
        if ((minInput && minInput.value.trim()) || (maxInput && maxInput.value.trim())) {
          count = 1;
        }
      } else {
        // 일반 필터: 체크된 체크박스 개수
        /** @type {NodeListOf<HTMLInputElement>} */
        const checkedInputs = panel.querySelectorAll('input[type="checkbox"]:checked');
        count = checkedInputs.length;

        // 캐스케이딩 카테고리: 3차 체크박스는 갯수대로, 3차 없이 1차만/2차만
        // 드릴인(선택)된 경우에도 1개로 표시한다(선택된 행은 aria-expanded=true).
        if (count === 0 && panel.querySelector('category-drilldown .cdf__row[aria-expanded="true"]')) {
          count = 1;
        }
      }

      totalActiveFilters += count;
      this.#applyMobileButtonCount(button, count);
    });

    // 리셋 버튼 표시/숨김 업데이트
    const resetWrapper = document.querySelector('.facets-mobile-bar__reset-wrapper');
    if (resetWrapper) {
      resetWrapper.classList.toggle('facets-mobile-bar__reset-wrapper--hidden', totalActiveFilters === 0);
    }
  }

  /**
   * 모바일 필터 버튼 하나의 카운트 뱃지(span·active class·data-filter-count)를 설정한다.
   * @param {HTMLElement} button
   * @param {number} count
   */
  #applyMobileButtonCount(button, count) {
    button.dataset.filterCount = String(count);
    let countSpan = button.querySelector('.facets-mobile-bar__filter-count');
    if (count > 0) {
      if (!countSpan) {
        countSpan = document.createElement('span');
        countSpan.className = 'facets-mobile-bar__filter-count';
        // caret 아이콘 앞에 삽입
        const caret = button.querySelector('.svg-wrapper');
        if (caret) {
          button.insertBefore(countSpan, caret);
        } else {
          button.appendChild(countSpan);
        }
      }
      countSpan.textContent = count.toString();
      button.classList.add('facets-mobile-bar__filter-button--active');
    } else {
      if (countSpan) countSpan.remove();
      button.classList.remove('facets-mobile-bar__filter-button--active');
    }
  }

  /**
   * fetch 한 섹션 HTML 의 서버 계산 카운트로 모바일 필터 버튼 뱃지를 재확정한다.
   * 즉시 JS 재계산(#syncMobileFilterButtons)이 어긋나도 preview fetch 후 권위값으로 자동 보정되어
   * "반영 안됨 → 재오픈해야 반영" 문제를 없앤다. (see-results 카운트와 동일한 즉시+보정 패턴)
   * @param {string} html
   */
  #reconcileMobileButtonsFromHTML(html) {
    const mobileBar = document.querySelector('.facets-mobile-bar__filters');
    if (!mobileBar) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const srcBar = doc.querySelector('.facets-mobile-bar__filters');
    if (!srcBar) return;
    mobileBar.querySelectorAll('.facets-mobile-bar__filter-button').forEach((button) => {
      const param = button.getAttribute('data-filter-param-name');
      if (!param) return;
      const src = srcBar.querySelector(`.facets-mobile-bar__filter-button[data-filter-param-name="${param}"]`);
      if (!src) return;
      const count = parseInt(src.getAttribute('data-filter-count') || '0', 10) || 0;
      this.#applyMobileButtonCount(/** @type {HTMLElement} */ (button), count);
    });
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    fetchResultsHTML(this.sectionId, url, true);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #trackDebounce = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    // this.#initSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    if (this.#trackDebounce) clearTimeout(this.#trackDebounce);
  }

  // /**
  //  * Initialize the price range slider
  //  */
  // #initSlider() {
  //   const { sliderMin, sliderMax, sliderProgress } = this.refs;
  //   if (!sliderMin || !sliderMax || !sliderProgress) return;

  //   sliderMin.addEventListener('input', this.#onSliderInput);
  //   sliderMax.addEventListener('input', this.#onSliderInput);

  //   this.#updateSliderRange();
  // }

  // /**
  //  * Handles slider input events (while dragging)
  //  * @param {Event} event - The input event
  //  */
  // #onSliderInput = (event) => {
  //   const { sliderMin, sliderMax } = this.refs;
  //   if (!sliderMin || !sliderMax) return;

  //   let minVal = Number(sliderMin.value);
  //   let maxVal = Number(sliderMax.value);

  //   if (minVal > maxVal) {
  //     if (event.target === sliderMin) {
  //       sliderMin.value = maxVal.toString();
  //     } else {
  //       sliderMax.value = minVal.toString();
  //     }
  //   }

  //   this.#updateSliderRange();
  // };

  // /**
  //  * Updates the visual range bar between the two slider thumbs
  //  */
  // #updateSliderRange() {
  //   const { sliderMin, sliderMax, sliderProgress } = this.refs;
  //   if (!sliderMin || !sliderMax || !sliderProgress) return;

  //   const min = Number(sliderMin.min);
  //   const max = Number(sliderMin.max);
  //   const minVal = Number(sliderMin.value);
  //   const maxVal = Number(sliderMax.value);

  //   const leftPercent = ((minVal - min) / (max - min)) * 100;
  //   const rightPercent = ((maxVal - min) / (max - min)) * 100;

  //   sliderProgress.style.left = `${leftPercent}%`;
  //   sliderProgress.style.right = `${100 - rightPercent}%`;
  // }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   * @param {Event} event - The change event from the input
   */
  updatePriceFilterAndResults(event) {
    const { minInput, maxInput } = this.refs;
    // event.target이 Proxy로 인해 price-facet-component를 가리킬 수 있으므로
    // document.activeElement를 사용하여 실제 포커스된 input을 확인
    const activeElement = document.activeElement;
    const changedInput = activeElement === minInput || activeElement === maxInput
      ? activeElement
      : null;

    // min > max 유효성 검사: adjustToValidValues 전에 먼저 검사
    // (adjustToValidValues가 값을 수정하기 전에 원본 값으로 비교해야 함)
    this.#validateMinMax(changedInput);

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();

    // 트래킹: min/max 연속 blur 가 있을 수 있으므로 600ms trailing debounce
    if (this.#trackDebounce) clearTimeout(this.#trackDebounce);
    this.#trackDebounce = setTimeout(() => {
      if (!window.GBTrack) return;
      const parsePrice = (raw) => {
        if (!raw) return null;
        const num = Number(raw.replace(/[^0-9.,]/g, '').replace(',', '.'));
        return Number.isFinite(num) ? num : null;
      };
      window.GBTrack.track('search_price_filter_apply', {
        tracking_id: _facetsTrackingId(),
        min_value: parsePrice(minInput.value),
        max_value: parsePrice(maxInput.value),
        placement: _facetsPlacement(this),
      });
    }, 600);
  }

  /**
   * Validates that min is not greater than max
   * If min > max, clears the value of the input that was changed last
   * @param {Element | null} changedInput - The input that was changed
   */
  #validateMinMax(changedInput) {
    const { minInput, maxInput } = this.refs;

    const minValue = minInput.value.trim();
    const maxValue = maxInput.value.trim();

    // 둘 다 값이 있을 때만 검사
    if (minValue === '' || maxValue === '') {
      return;
    }

    const min = Number(minValue.replace(/[^0-9.,]/g, '').replace(',', '.'));
    const max = Number(maxValue.replace(/[^0-9.,]/g, '').replace(',', '.'));

    if (isNaN(min) || isNaN(max)) {
      return;
    }

    // min이 max보다 크면 나중에 입력한 값을 비움
    if (min > max) {
      if (changedInput === maxInput) {
        // max를 나중에 입력했는데 min보다 작으면 max를 비움
        maxInput.value = '';
      } else if (changedInput === minInput) {
        // min을 나중에 입력했는데 max보다 크면 min을 비움
        minInput.value = '';
      }
    }
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    const details = event.target.closest('details');
    const accordion = event.target.closest('accordion-custom');

    if (window.GBTrack) {
      const checkedBefore = container?.querySelectorAll('[type="checkbox"]:checked').length || 0;
      const priceFilled =
        container?.tagName === 'PRICE-FACET-COMPONENT' &&
        Array.from(container.querySelectorAll('input')).some(
          (i) => i instanceof HTMLInputElement && i.value.trim() !== ''
        )
          ? 1
          : 0;
      const filterParamName =
        (accordion instanceof HTMLElement ? accordion.dataset.filterParamName : null) ||
        (details instanceof HTMLElement ? details.dataset.filterParamName : null) ||
        null;
      window.GBTrack.track('search_filter_clear_click', {
        tracking_id: _facetsTrackingId(),
        filter_label: details instanceof HTMLElement ? details.dataset.filterLabel || null : null,
        filter_param_name: filterParamName,
        cleared_value_count: checkedBefore || priceFilled,
        placement: _facetsPlacement(event.target),
      });
    }

    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    const shouldShow = event.shouldShowClearAll();
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', shouldShow);
    }
    // Also handle mobile bar reset wrapper visibility
    if (this.classList.contains('facets-mobile-bar__reset-wrapper')) {
      this.classList.toggle('facets-mobile-bar__reset-wrapper--hidden', !shouldShow);
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality with desktop popover and mobile bottom sheet
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  /** @type {((event: Event) => void) | null} */
  #boundHandleOutsideClick = null;

  connectedCallback() {
    super.connectedCallback();
    this.#boundHandleOutsideClick = this.#handleOutsideClick.bind(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#boundHandleOutsideClick) {
      document.removeEventListener('click', this.#boundHandleOutsideClick);
    }
  }

  /**
   * Gets popover element from refs
   * @returns {HTMLElement | null}
   */
  #getPopover() {
    const popover = this.refs.popover;
    if (popover instanceof HTMLElement) return popover;
    if (Array.isArray(popover) && popover[0] instanceof HTMLElement) return popover[0];
    return null;
  }

  /**
   * Gets popover trigger element from refs
   * @returns {HTMLElement | null}
   */
  #getPopoverTrigger() {
    const trigger = this.refs.popoverTrigger;
    if (trigger instanceof HTMLElement) return trigger;
    if (Array.isArray(trigger) && trigger[0] instanceof HTMLElement) return trigger[0];
    return null;
  }

  /**
   * Gets backdrop element from refs
   * @returns {HTMLElement | null}
   */
  #getBackdrop() {
    const backdrop = this.refs.backdrop;
    if (backdrop instanceof HTMLElement) return backdrop;
    if (Array.isArray(backdrop) && backdrop[0] instanceof HTMLElement) return backdrop[0];
    return null;
  }

  /**
   * Gets bottom sheet element from refs
   * @returns {HTMLElement | null}
   */
  #getBottomSheet() {
    const bottomSheet = this.refs.bottomSheet;
    if (bottomSheet instanceof HTMLElement) return bottomSheet;
    if (Array.isArray(bottomSheet) && bottomSheet[0] instanceof HTMLElement) return bottomSheet[0];
    return null;
  }

  /**
   * Gets current value element from refs
   * @returns {HTMLElement | null}
   */
  #getCurrentValue() {
    const el = this.refs.currentValue;
    if (el instanceof HTMLElement) return el;
    if (Array.isArray(el) && el[0] instanceof HTMLElement) return el[0];
    return null;
  }

  /**
   * Gets mobile current value element from refs
   * @returns {HTMLElement | null}
   */
  #getMobileCurrentValue() {
    const el = this.refs.mobileCurrentValue;
    if (el instanceof HTMLElement) return el;
    if (Array.isArray(el) && el[0] instanceof HTMLElement) return el[0];
    return null;
  }

  /**
   * Toggles the desktop popover
   */
  toggleSortPopover = () => {
    const popover = this.#getPopover();
    if (!popover) return;

    const isOpen = !popover.hidden;

    if (isOpen) {
      this.#closePopover('toggle');
    } else {
      this.#openPopover();
      this.#trackSortOpen('desktop_popover');
    }
  };

  /**
   * Opens the desktop popover
   */
  #openPopover() {
    const popover = this.#getPopover();
    const popoverTrigger = this.#getPopoverTrigger();
    if (!popover || !popoverTrigger) return;

    popover.hidden = false;
    popoverTrigger.setAttribute('aria-expanded', 'true');

    // Add outside click listener
    if (this.#boundHandleOutsideClick) {
      requestAnimationFrame(() => {
        if (this.#boundHandleOutsideClick) {
          document.addEventListener('click', this.#boundHandleOutsideClick);
        }
      });
    }

    // Focus selected option
    const selectedOption = popover.querySelector('[aria-selected="true"]');
    if (selectedOption instanceof HTMLElement) {
      selectedOption.focus();
    }
  }

  /**
   * Closes the desktop popover
   * @param {string} [reason]
   */
  #closePopover(reason = 'outside_click') {
    const popover = this.#getPopover();
    const popoverTrigger = this.#getPopoverTrigger();
    if (!popover || !popoverTrigger) return;
    if (popover.hidden) return;

    popover.hidden = true;
    popoverTrigger.setAttribute('aria-expanded', 'false');
    if (this.#boundHandleOutsideClick) {
      document.removeEventListener('click', this.#boundHandleOutsideClick);
    }
    this.#trackSortClose('desktop_popover', reason);
  }

  /**
   * Handles clicks outside the popover to close it
   * @param {Event} event - The click event
   */
  #handleOutsideClick(event) {
    const popover = this.#getPopover();
    const popoverTrigger = this.#getPopoverTrigger();
    if (!popover || !popoverTrigger) return;

    const target = event.target;
    if (!(target instanceof Node)) return;

    if (!popover.contains(target) && !popoverTrigger.contains(target)) {
      this.#closePopover('outside_click');
    }
  }

  /**
   * Opens the mobile bottom sheet
   */
  openBottomSheet = () => {
    const backdrop = this.#getBackdrop();
    const bottomSheet = this.#getBottomSheet();
    if (!backdrop || !bottomSheet) return;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Use class-based animation for smooth transition
    requestAnimationFrame(() => {
      backdrop.classList.add('is-open');
      bottomSheet.classList.add('is-open');
    });

    this.#trackSortOpen('mobile_bottom_sheet');
  };

  /**
   * Closes the mobile bottom sheet
   * @param {string} [reason]
   */
  closeBottomSheet = (reason = 'backdrop') => {
    const backdrop = this.#getBackdrop();
    const bottomSheet = this.#getBottomSheet();
    if (!backdrop || !bottomSheet) return;

    const wasOpen = bottomSheet.classList.contains('is-open');

    backdrop.classList.remove('is-open');
    bottomSheet.classList.remove('is-open');

    // Wait for animation to complete before restoring body scroll
    setTimeout(() => {
      document.body.style.overflow = '';
    }, 300);

    if (wasOpen) this.#trackSortClose('mobile_bottom_sheet', reason);
  };

  /**
   * 정렬 트래킹: open
   * @param {'desktop_popover' | 'mobile_bottom_sheet'} placement
   */
  #trackSortOpen(placement) {
    if (!window.GBTrack) return;
    window.GBTrack.track('search_sort_open', {
      tracking_id: _facetsTrackingId(),
      placement,
      current_sort: this.#currentSortValue(),
    });
  }

  /**
   * 정렬 트래킹: close
   * @param {'desktop_popover' | 'mobile_bottom_sheet'} placement
   * @param {string} reason - option_select | outside_click | escape | backdrop | toggle
   */
  #trackSortClose(placement, reason) {
    if (!window.GBTrack) return;
    window.GBTrack.track('search_sort_close', {
      tracking_id: _facetsTrackingId(),
      placement,
      reason,
    });
  }

  /**
   * 현재 선택된 sort_by 값
   * @returns {string | null}
   */
  #currentSortValue() {
    const checked = this.querySelector('input[name="sort_by"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : null;
  }

  /**
   * Selects a sort option from popover or bottom sheet
   * @param {Event} event - The click event
   */
  selectSortOption = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const optionButton = target.closest('[data-value]');
    if (!optionButton) return;

    const value = optionButton.getAttribute('data-value');
    if (!value) return;

    const isTabletOrMobile = window.innerWidth < 1024;
    const placement = isTabletOrMobile ? 'mobile_bottom_sheet' : 'desktop_popover';
    const fromSortBy = this.#currentSortValue();
    const optionSelector = isTabletOrMobile
      ? '.sorting-filter__bottom-sheet-option'
      : '.sorting-filter__option';
    const allOptions = Array.from(this.querySelectorAll(optionSelector));
    const optionIndex = allOptions.indexOf(/** @type {Element} */ (optionButton)) + 1;

    // Update hidden radio input
    const radioInputs = this.querySelectorAll('input[name="sort_by"]');
    radioInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.value === value;
      }
    });

    // Update UI - current value display
    const optionText = optionButton.querySelector('.sorting-filter__option-text')?.textContent || '';
    const currentValue = this.#getCurrentValue();
    const mobileCurrentValue = this.#getMobileCurrentValue();
    if (currentValue) currentValue.textContent = optionText;
    if (mobileCurrentValue) mobileCurrentValue.textContent = optionText;

    // Update aria-selected states and checkmarks
    this.#updateSelectedState(value);

    if (window.GBTrack) {
      window.GBTrack.track('search_sort_option_click', {
        tracking_id: _facetsTrackingId(),
        placement,
        from_sort_by: fromSortBy,
        to_sort_by: value,
        index: optionIndex > 0 ? optionIndex : null,
      });
    }

    // Close popover or bottom sheet (tablet and mobile use bottom sheet)
    if (isTabletOrMobile) {
      this.closeBottomSheet('option_select');
    } else {
      this.#closePopover('option_select');
    }

    // Trigger filter update
    this.#triggerFilterUpdate();
  };

  /**
   * Updates the selected state UI for all options
   * @param {string} value - The selected value
   */
  #updateSelectedState(value) {
    // Update popover options
    const popoverOptions = this.querySelectorAll('.sorting-filter__option');
    popoverOptions.forEach((opt) => {
      const isSelected = opt.getAttribute('data-value') === value;
      opt.setAttribute('aria-selected', isSelected.toString());

      // Update checkmark
      const existingCheck = opt.querySelector('.sorting-filter__check');
      if (isSelected && !existingCheck) {
        const checkSpan = document.createElement('span');
        checkSpan.className = 'sorting-filter__check svg-wrapper';
        checkSpan.innerHTML = this.#getCheckmarkSvg();
        opt.appendChild(checkSpan);
      } else if (!isSelected && existingCheck) {
        existingCheck.remove();
      }
    });

    // Update bottom sheet options
    const bottomSheetOptions = this.querySelectorAll('.sorting-filter__bottom-sheet-option');
    bottomSheetOptions.forEach((opt) => {
      const isSelected = opt.getAttribute('data-value') === value;
      opt.setAttribute('aria-selected', isSelected.toString());

      // Update checkmark
      const existingCheck = opt.querySelector('.sorting-filter__check');
      if (isSelected && !existingCheck) {
        const checkSpan = document.createElement('span');
        checkSpan.className = 'sorting-filter__check svg-wrapper';
        checkSpan.innerHTML = this.#getCheckmarkSvg();
        opt.appendChild(checkSpan);
      } else if (!isSelected && existingCheck) {
        existingCheck.remove();
      }
    });
  }

  /**
   * Returns the checkmark SVG markup
   * @returns {string} The SVG markup
   */
  #getCheckmarkSvg() {
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  }

  /**
   * Triggers the filter update
   */
  #triggerFilterUpdate() {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    facetsForm.updateFiltersImmediate();
  }

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const popover = this.#getPopover();
    if (!popover || popover.hidden) return;

    const options = Array.from(popover.querySelectorAll('[role="option"]'));
    const currentFocused = document.activeElement;
    let currentIndex = options.findIndex((opt) => opt === currentFocused);
    if (currentIndex === -1) currentIndex = 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = Math.min(currentIndex + 1, options.length - 1);
        if (options[nextIndex] instanceof HTMLElement) {
          options[nextIndex].focus();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = Math.max(currentIndex - 1, 0);
        if (options[prevIndex] instanceof HTMLElement) {
          options[prevIndex].focus();
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closePopover('escape');
        this.#getPopoverTrigger()?.focus();
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        if (currentFocused instanceof HTMLElement && currentFocused.hasAttribute('data-value')) {
          currentFocused.click();
        }
        break;
    }
  };

  /**
   * Updates filter and sorting (legacy method for compatibility)
   */
  updateFilterAndSorting() {
    this.#triggerFilterUpdate();
  }

  /**
   * Updates the facet status (legacy method for compatibility)
   */
  updateFacetStatus() {
    // No longer needed with new UI, but kept for compatibility
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

// ─── 필터 드로어 라이프사이클 트래킹 ───────────────────────────────────────
// `<dialog-component id="filters-drawer">` 의 `dialog:open` / `dialog:close` 이벤트를
// 받아 search_filter_drawer_open / _close 발사. 트리거 element 의
// `data-track-drawer-trigger` 속성으로 entry_point 식별.
(function initFiltersDrawerTracking() {
  /** @type {{ entry_point: string, filter_param_name: string | null }} */
  let lastOpenContext = { entry_point: 'unknown', filter_param_name: null };

  // capture 페이즈에서 트리거 클릭을 잡아 다음 dialog:open 의 컨텍스트로 저장
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const trigger = target.closest('[data-track-drawer-trigger]');
      if (!(trigger instanceof HTMLElement)) return;
      lastOpenContext = {
        entry_point: trigger.dataset.trackDrawerTrigger || 'unknown',
        filter_param_name: trigger.dataset.filterParamName || null,
      };
    },
    true
  );

  function bindDrawer(drawer) {
    if (!(drawer instanceof HTMLElement)) return;
    if (drawer.dataset.gbTrackBound === 'true') return;
    drawer.dataset.gbTrackBound = 'true';

    drawer.addEventListener('dialog:open', () => {
      if (!window.GBTrack) return;
      window.GBTrack.track('search_filter_drawer_open', {
        tracking_id: _facetsTrackingId(),
        entry_point: lastOpenContext.entry_point,
        filter_param_name: lastOpenContext.filter_param_name,
      });
      // 다음 open 을 위해 reset
      lastOpenContext = { entry_point: 'unknown', filter_param_name: null };
    });

    drawer.addEventListener('dialog:close', () => {
      if (!window.GBTrack) return;
      window.GBTrack.track('search_filter_drawer_close', {
        tracking_id: _facetsTrackingId(),
        reason: _facetsDrawerCloseReason || 'cancel',
      });
      _facetsDrawerCloseReason = null;
    });
  }

  function init() {
    bindDrawer(document.getElementById('filters-drawer'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // dialog-component 의 connectedCallback 이 facets.js 모듈 평가 이후에 일어나거나
  // 페이지가 morph 되어 drawer 가 새로 들어오는 경우를 위한 폴백.
  if ('MutationObserver' in window) {
    new MutationObserver(() => {
      const drawer = document.getElementById('filters-drawer');
      if (drawer && drawer.dataset.gbTrackBound !== 'true') bindDrawer(drawer);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
